/**
 * `club:react` — web -> native (contract C5). Body `{ swimId, emoji | null }`;
 * null removes the viewer's reaction on that swim.
 *
 * Goes out as a plain bridge envelope (`{ v: 1, method: "clubReact", args,
 * id }`, the same shape every other method uses). The reply's `data.ok` is the
 * confirmation; the next `club:snapshot` carries the reaction back. In a browser there is no handler: the tap stays local and the
 * result says so (`delivered: false`) instead of pretending.
 */
import type { BridgeRequest } from "@contract/types";
import type { ClubReactBody } from "./snapshot";

/** The bridge METHOD is `clubReact` (BridgeRouter case + BridgeVersion list on the
 *  preview branch; club-sync's `BridgeHandlers.clubReact`). Contract C5 wrote the
 *  message as `club:react`; that is its NAME in the contract prose, not the
 *  method string. Sending "club:react" got BRIDGE_METHOD_UNKNOWN on the phone
 *  and every tap rolled back (merged-preview defect, 2026-09-10 01:5x). */
export const CLUB_REACT_METHOD = "clubReact";

/** Something that takes a bridge envelope and answers. The real one is
 *  `window.webkit.messageHandlers.swimset.postMessage`; tests pass a fixture. */
export type EnvelopePoster = (envelope: BridgeRequest) => Promise<unknown>;

export interface ReactResult {
  /** The native side confirmed (or there was nobody to confirm, see delivered). */
  ok: boolean;
  /** False in a browser: no shell handler, nothing was sent. */
  delivered: boolean;
  error?: string;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function reactEnvelope(body: ClubReactBody, id: string = newId()): BridgeRequest {
  return { v: 1, method: CLUB_REACT_METHOD, args: { swimId: body.swimId, emoji: body.emoji }, id };
}

function shellPoster(): EnvelopePoster | null {
  if (typeof window === "undefined") return null;
  const handler = window.webkit?.messageHandlers?.swimset;
  if (!handler || typeof handler.postMessage !== "function") return null;
  return (env) => handler.postMessage(env);
}

/** Send one reaction. Never throws. */
export async function sendReact(body: ClubReactBody, poster: EnvelopePoster | null = shellPoster()): Promise<ReactResult> {
  if (!poster) return { ok: true, delivered: false };
  try {
    // Two layers: the ENVELOPE's ok (the call was routed and answered) and the
    // reply DATA's ok (native confirmed the CloudKit save). Only both mean saved;
    // `{ ok:false }` in data is how native says "not confirmed this pass".
    const raw = (await poster(reactEnvelope(body))) as
      | { ok?: unknown; data?: { ok?: unknown } | null; error?: { code?: unknown; message?: unknown } }
      | null;
    if (raw && raw.ok === true) {
      if (raw.data && raw.data.ok === true) return { ok: true, delivered: true };
      return { ok: false, delivered: true, error: "native did not confirm the reaction" };
    }
    const message = raw?.error && typeof raw.error.message === "string" ? raw.error.message : "clubReact was not answered";
    return { ok: false, delivered: true, error: message };
  } catch (e) {
    return { ok: false, delivered: true, error: e instanceof Error ? e.message : String(e) };
  }
}
